import { FilterSpecification, type Map as MaplibreMap } from "maplibre-gl";

import { Timezone } from "@/constants/dateTime";
import { SUBLAYERS } from "@/constants/map";
import {
  DataSchemaType,
  Dataset,
  DatasetAttributeSchema,
  DatasetTimeseriesData,
  TimeseriesType
} from "@/types/dataset";
import { LayerType } from "@/types/map";
import { LayerSublayers, NON_POI_LAYER_TYPES } from "@/utils/map";

import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);
dayjs.extend(timezone);

const orderDays = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday"
];

export const handleFilterFeaturesGroupByAttribute = (
  map: MaplibreMap | null,
  name: string,
  value: string,
  layerId: string,
  options?: {
    // Optional legend-filter expression to AND with the time filter so a
    // time-scrub doesn't wipe the user's legend hide/show selection. When
    // omitted, only the time `==` filter is applied (legacy behavior).
    extraFilter?: unknown;
    // When provided, the composed filter is applied to every data-driven
    // sublayer of the dataset: POINT datasets use circle/unclustered/label/rank,
    // all others use fill/outline/label/rank/stroke — for parity with
    // handleFilterLayerByLegend. When omitted, the filter is applied to the
    // first layer whose id starts with `layerId` (legacy behavior).
    dataset?: Dataset;
  }
) => {
  if (!map || !value) return;

  const style = map.getStyle();

  if (!style) return;

  const isNumber = value !== "" && !Number.isNaN(Number(value));
  const parsedValue = isNumber ? Number(value) : value;
  const timeFilter: unknown = ["==", ["get", name], parsedValue];

  const composedFilter = options?.extraFilter
    ? ["all", options.extraFilter, timeFilter]
    : timeFilter;

  if (options?.dataset) {
    const spatialType = options.dataset.properties.spatialType;
    const sublayers: LayerSublayers = SUBLAYERS(options.dataset.data.id)[
      spatialType
    ];
    // POINT datasets render their geometry in circle/unclustered, not
    // fill/outline — using NON_POI_LAYER_TYPES would filter only their labels
    // and leave the visible points unfiltered.
    const layerTypes: (keyof LayerSublayers)[] =
      spatialType === LayerType.POINT
        ? ["circle", "unclustered", "label", "rank"]
        : NON_POI_LAYER_TYPES;
    layerTypes.forEach((layerKey) => {
      const sublayerId = sublayers?.[layerKey];
      // Guard with getLayer: not every sublayer in SUBLAYERS is necessarily
      // present in the live style (e.g. a sublayer that wasn't rendered yet),
      // and setFilter throws on a non-existing layer.
      if (sublayerId && map.getLayer(sublayerId)) {
        map.setFilter(sublayerId, composedFilter as FilterSpecification);
      }
    });
    return;
  }

  const matchLayerId = style.layers.find((layer) =>
    layer.id.startsWith(layerId)
  );

  if (!matchLayerId) return;

  map.setFilter(matchLayerId.id, composedFilter as FilterSpecification);
};

export const getTypeTimeSeries = (
  categories: DatasetAttributeSchema[]
): TimeseriesType => {
  const groupByCategories = categories.find((cat) => cat.group_by_attr);

  if (!groupByCategories) return TimeseriesType.TIME; // default to time if no groupByCategories found

  const category = groupByCategories;

  if (category?.data_type === DataSchemaType.TIME) {
    return TimeseriesType.TIME;
  } else if (category?.data_type === DataSchemaType.TEXT) {
    return TimeseriesType.DAY;
  } else if (category?.data_type === DataSchemaType.INT) {
    return TimeseriesType.WEEK;
  } else if (category?.data_type === DataSchemaType.DATE) {
    return TimeseriesType.DATE;
  } else {
    return TimeseriesType.TIME; // default to time if type is unknown
  }
};

export const sortTimeSeriesData = (
  data: DatasetTimeseriesData[],
  type: TimeseriesType,
  timezone?: Timezone
): DatasetTimeseriesData[] => {
  if (type === TimeseriesType.DAY) {
    return [...data].sort(
      (a, b) => orderDays.indexOf(a.time) - orderDays.indexOf(b.time)
    );
  } else if (type === TimeseriesType.WEEK) {
    return [...data].sort((a, b) => Number(a.time) - Number(b.time));
  } else if (type === TimeseriesType.DATE) {
    return [...data].sort(
      (a, b) => dayjs(a.time).valueOf() - dayjs(b.time).valueOf()
    );
  } else {
    return [...data].sort((a, b) => {
      const hourA = dayjs.utc(a.time).tz(timezone?.code).hour();
      const hourB = dayjs.utc(b.time).tz(timezone?.code).hour();

      return hourA - hourB;
    });
  }
};
